import { useEffect, useState } from 'react';
import { Sheet } from '../../components/Sheet';
import { Button } from '../../components/ui';
import { useT } from '../../i18n';
import { toast } from '../../stores/toasts';

/** The run's share picture: share it (Zalo, Facebook, Instagram…) or save it to the phone. */
export function ShareRunSheet({ image, fileName, onClose }: { image: Blob | null; fileName: string; onClose: () => void }) {
  const t = useT();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!image) return;
    const u = URL.createObjectURL(image);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [image]);

  const file = image ? new File([image], fileName, { type: 'image/png' }) : null;
  const canShareFile = !!file && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });

  async function share() {
    if (!file) return;
    try {
      await navigator.share({ files: [file] });
    } catch (e) {
      // Closing the share sheet is not an error.
      if ((e as Error).name !== 'AbortError') toast({ kind: 'error', title: t('Could not share the image') });
    }
  }

  function download() {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  }

  return (
    <Sheet open={!!image} onClose={onClose} label={t('Share run')}>
      {image && url ? (
        <div className="space-y-3" data-testid="share-run-sheet">
          <img src={url} alt={t('Share run')} className="mx-auto max-h-[52dvh] rounded-2xl border border-ink-700" data-testid="share-run-image" />
          {canShareFile ? (
            <>
              <Button className="w-full" onClick={share} data-testid="share-run-share">
                {t('Share or save to Photos')}
              </Button>
              <p className="text-center text-xs text-ink-400">{t('On iPhone: tap Share, then “Save Image” to keep it in Photos.')}</p>
            </>
          ) : null}
          <Button variant="secondary" className="w-full" onClick={download} data-testid="share-run-download">
            {t('Download image')}
          </Button>
        </div>
      ) : null}
    </Sheet>
  );
}
